import { useUser } from '@clerk/react';
import { peekOnboardingReturn } from '@/containers/onboarding/onboarding-return';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { useFeatureAccess } from '@/hooks/use-feature-access.hook';
import {
  bookingPending,
  clearBookingPending,
  clearTrialOfferPending,
  trialParam,
  trialOfferPending,
} from '@/lib/card-trial';
import { useAppDispatch } from '@/store';
import {
  setAiChatInitialMessage,
  setAiChatOpen,
} from '@/store/features/ui.feature';

import { PostTrialBooking } from './post-trial-booking.component';
import { TrialActivateGate } from './trial-activate-gate.component';
import { TrialExpiredGate } from './trial-expired-gate.component';

// One id, so StrictMode's second effect run replaces the toast instead of
// stacking a copy.
const RETURN_TOAST_ID = 'card-trial-return';

interface TrialAccessBoundaryProps {
  children: React.ReactNode;
  /** Authentication and provider callback routes must complete even while an
   * organization is awaiting a plan decision. Keep this list explicit at the
   * root rather than letting feature routes opt themselves out. */
  bypass?: boolean;
  /** Onboarding finishes first; the card-required trial offer follows it. */
  deferTrialOffer?: boolean;
}

/**
 * Swaps the app for the mandatory plan chooser when the active organization's
 * trial has ended without a plan decision.
 *
 * Deliberately fail-open on loading and errors: orgs that never had a trial —
 * every existing free, paid, and AppSumo org — must boot exactly as they did
 * before trials existed, with no blocking frame while entitlements load. An
 * expired-trial org may glimpse the app for the moment the payload takes to
 * arrive; that is acceptable because the chooser is UX, not the fence — the
 * backend enforces every entitlement server-side regardless of what mounts.
 */
export function TrialAccessBoundary({
  children,
  bypass = false,
  deferTrialOffer = false,
}: TrialAccessBoundaryProps) {
  // Reads its markers from storage on every render; the React Compiler would
  // cache those reads, missing the ones onboarding writes after first render.
  'use no memo';
  const { trial, isReady, isFetching, reducedCreditCountry } =
    useFeatureAccess();
  const { user } = useUser();
  // Fail-closed, like the trial status bar: hidden until the entitlements say
  // the org is not from a reduced-credit country.
  const bookingEnabled = reducedCreditCountry === false;
  const dispatch = useAppDispatch();
  // Read once, before the effect drops it: the offer gate mounts only after
  // the (cold, post-redirect) entitlements arrive, when the URL has no
  // `?trial=` any more.
  const [returned] = useState(trialParam);
  const [offerClosed, setOfferClosed] = useState(false);
  // Onboarding's own trial page takes a closed Checkout back, not this gate.
  const [onboardingOwnsReturn] = useState(
    () => peekOnboardingReturn() === 'trial'
  );
  const [bookingClosed, setBookingClosed] = useState(false);
  const [bookingShown, setBookingShown] = useState(false);

  // A reduced-credit-country org never shows the call: drop the marker rather
  // than keep it around.
  useEffect(() => {
    if (reducedCreditCountry === true) clearBookingPending();
  }, [reducedCreditCountry]);

  // Back from Stripe: confirm a started trial once, reopen the Clu welcome
  // onboarding opened before the offer (lost to the redirect), then drop
  // `?trial=` so a reload doesn't repeat any of it.
  useEffect(() => {
    const result = returned;
    if (!result) return;
    if (result === 'error') {
      toast.error("We couldn't start your trial", {
        id: RETURN_TOAST_ID,
        description:
          'Please try again, or reply to any of our emails for help.',
      });
    }
    // The API refused the saved card and cancelled it; nothing was charged.
    if (result === 'card_in_use') {
      toast.error('This card is already used on another account', {
        id: RETURN_TOAST_ID,
        description:
          "Each card can start one free trial. If that's a mistake, email support@cleanlist.ai and we'll look into your accounts.",
        duration: 15000,
      });
    }
    if (result === 'prepaid_card') {
      toast.error("Prepaid cards can't start a trial", {
        id: RETURN_TOAST_ID,
        description: 'Please use a credit or debit card.',
      });
    }
    if (result === 'activated') {
      toast.success('Your 14-day trial is live', {
        id: RETURN_TOAST_ID,
        description:
          trial?.plan === 'pro'
            ? 'Your trial is ready. Build and save your workflows, then choose a paid plan to launch.'
            : "Every feature is unlocked. We'll email you 3 days before it ends.",
      });
      // Only the offer that followed onboarding had the welcome open.
      if (trialOfferPending()) {
        dispatch(setAiChatInitialMessage('Hey Clu, what can you do?'));
        dispatch(setAiChatOpen(true));
      }
      clearTrialOfferPending();
    }
    const url = new URL(window.location.href);
    url.searchParams.delete('trial');
    window.history.replaceState(window.history.state, '', url);
  }, [dispatch, returned, trial?.plan]);

  if (bypass) return children;

  if (trial?.must_choose_plan) {
    return <TrialExpiredGate />;
  }

  // Full screen after onboarding or a closed Checkout; otherwise the banner.
  const offerShown =
    !offerClosed &&
    !onboardingOwnsReturn &&
    (trialOfferPending() || returned === 'cancelled');
  if (trial?.card_required && !deferTrialOffer && offerShown) {
    return (
      <TrialActivateGate
        cancelled={returned === 'cancelled'}
        onContinueFree={() => {
          clearTrialOfferPending();
          setOfferClosed(true);
        }}
      />
    );
  }

  // The onboarding call, after the trial decision (Free, or back from Stripe),
  // or straight after onboarding once fresh entitlements show no offer. Once
  // up, a background refetch never takes it down.
  const bookingDue =
    offerClosed || returned === 'activated' || bookingShown || !isFetching;
  if (
    !bookingClosed &&
    bookingEnabled &&
    isReady &&
    bookingDue &&
    !deferTrialOffer &&
    bookingPending(user?.id)
  ) {
    if (!bookingShown) setBookingShown(true);
    return (
      <PostTrialBooking
        onTrial={Boolean(trial?.active && !trial.card_required)}
        onDone={() => {
          clearBookingPending();
          setBookingClosed(true);
        }}
      />
    );
  }

  return children;
}
